// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
// RxJS
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
// Services and Models
import {
  ContactRelationshipsService,
  PractitionRoleService,
  selectContactRelationshipLatestSuccessfullAction,
  selectContactRelationshipsActionLoading,
  ViewContext,
} from '../../../../../core/lists';
import { skip } from 'rxjs/operators';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-filter-contact-add',
  templateUrl: './filtered-view-contact-relation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilteredViewContactRelationDialogComponent
  implements AfterViewInit, OnInit, OnDestroy
{
  // Public properties
  relationshipEditForm: FormGroup;
  viewLoading = true;
  loadingAfterSubmit = false;
  filteredEntities: any[];
  entityType = 'DOCTOR';
  category = '';
  relationCategoryList = [];
  relationCategoryDescriptionList = [];
  private subscriptions: any[] = [];
  entityAutocompleteCall = this.contactRelationshipsService.searchLists.bind(
    this.contactRelationshipsService,
    ViewContext.patients,
  );
  filteredOptions = [];

  /**
   * Component constructor
   *
   * @param dialogRef: MatDialogRef<PatientRelationshipEditComponent>
   * @param data: any
   * @param fb: FormBuilder
   * @param contactRelationshipsService: ContactRelationshipsService
   * @param cdr: ChangeDetectorRef
   */
  constructor(
    public dialogRef: MatDialogRef<FilteredViewContactRelationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private practitionRoleService: PractitionRoleService,
    private contactRelationshipsService: ContactRelationshipsService,
    private cdr: ChangeDetectorRef,
    public stringUtils: StringUtilsService,
    private store: Store<AppState>,
  ) {
    this.filteredOptions = this.data.value.related;
  }

  /**
   * On init
   */
  ngOnInit() {
    if (this.data.item) {
      this.entityType = this.data.item.entity_type;
    }
    const loadingSub = this.store
      .pipe(select(selectContactRelationshipsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);
    this.loadCategory();
    const successSubscription = this.store
      .pipe(select(selectContactRelationshipLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          this.dialogRef.close();
        }
      });
    this.subscriptions.push(successSubscription);
    this.initRelationshipForm();
    this.relationshipEditForm.get('relationCategory').valueChanges.subscribe((val) => {
      if (!val) return;
      this.category = val;
      this.contactRelationshipsService
        .getRelationTypeDescription(this.entityType, this.category)
        .subscribe((res) => {
          this.relationCategoryDescriptionList = res.data;
          if (this.data.item.relation_type_id) {
            const description = this.relationCategoryDescriptionList.find(
              (e: any) => e.id === this.data.item.relation_type_id,
            );
            this.relationshipEditForm.controls.relationCategoryDescription.setValue(description);
          }
        });
    });
    this.relationshipEditForm
      .get('relationCategory')
      .setValue(this.data.item ? this.data.item.relation_type : null);
    this.viewLoading = false;
    this.cdr.detectChanges();
    this.relationshipEditForm.get('entityInput').valueChanges.subscribe((name) => {
      if (name) {
        this.filteredOptions = this._filter(name);
      }
    });
  }

  private _filter(name: string): any[] {
    const filterValue = '' + name.toLowerCase();
    return this.filteredOptions.filter((option) => {
      const value = '' + option.first_name + '' + option.lastname + '' + option.last_name2;
      if (value.toLowerCase().includes(filterValue) || value.toLowerCase().includes(filterValue)) {
        return option;
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.data.item && this.data.item.entity_id) {
      const entity = {
        first_name: this.data.item && this.data.item.first_name ? this.data.item.first_name : '',
        id: this.data.item.entity_id,
        last_name: this.data.item && this.data.item.last_name ? this.data.item.last_name : '',
      };
      this.relationshipEditForm
        .get('entityInput')
        .setValue(this.data.item && this.data.item.entity_id ? entity : null);
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initRelationshipForm() {
    this.relationshipEditForm = this.fb.group({
      entity_type: [this.data.item ? this.data.item.entity_type : null],
      entityInput: [null, [Validators.required]],
      relationCategory: [null],
      relationCategoryDescription: [this.data.item ? this.data.item.relation_type_id : null],
      isPrimary: [this.data.item ? this.data.item.is_prime : false],
      financialResponsible: [this.data.item ? this.data.item.financial_responsible : false],
      emergencyContact: [this.data.item ? this.data.item.emergency_contact : false],
      phiAuthorized: [this.data.item ? this.data.item.phi_authorized : false],
    });
    if (!this.data.contactEntity) {
      this.relationshipEditForm.get('relationCategory').setValidators([Validators.required]);
      this.relationshipEditForm
        .get('relationCategory')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.relationshipEditForm
        .get('relationCategoryDescription')
        .setValidators([Validators.required]);
      this.relationshipEditForm.get('relationCategoryDescription').updateValueAndValidity({
        emitEvent: false,
        onlySelf: true,
      });
    }
  }

  selectType(entity_type) {
    this.entityType = entity_type;
    this.loadCategory();
    if (entity_type === 'FACILITY') entity_type = 'FACILITIE';
    this.entityAutocompleteCall = this.contactRelationshipsService.searchLists.bind(
      this.contactRelationshipsService,
      entity_type,
    );
  }

  /**
   * Close dialog
   */
  onNoClick(): void {
    this.dialogRef.close({ isUpdated: false });
  }

  /**
   * Save relationship
   */
  save() {
    const controls = this.relationshipEditForm.controls;
    if (this.relationshipEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }
    if (!this.data.contactEntity) {
      this.loadingAfterSubmit = true;
      this.viewLoading = true;
      const entity_id = controls.entityInput.value.id;
      const entity_type =
        controls.entityInput.value && controls.entityInput.value.entity_type
          ? controls.entityInput.value.entity_type
          : '';
      const relationship_description = controls.relationCategoryDescription.value.description_type;
      const relation_type_id = controls.relationCategoryDescription.value.id;
      const relation_type = controls.relationCategory.value;
      const is_prime = controls.isPrimary.value;
      const financial_responsible = controls.financialResponsible.value;
      const emergency_contact = controls.emergencyContact.value;
      const phi_authorized = controls.phiAuthorized.value;
      this.viewLoading = false;
      const response = {
        entity_id,
        entity_type,
        relation_type_id,
        relation_type,
        relationship_description,
        is_prime,
        phi_authorized,
        emergency_contact,
        financial_responsible,
      };
      this.closeDialog(response);
    }
  }

  /**
   * Close dialog
   *
   * @param specId: any
   */
  closeDialog(response) {
    this.dialogRef.close({
      done: true,
      response,
    });
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.relationshipEditForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  getTitle(): string {
    if (this.data.item) return 'CONTACT_RELATIONSHIP.EDIT_CONTACT_RELATIONSHIP';
    return 'CONTACT_RELATIONSHIP.NEW_CONTACT_RELATIONSHIP';
  }

  loadCategory() {
    this.contactRelationshipsService.getRelationTypesCategory(this.entityType).subscribe((res) => {
      if (!res) return;
      this.relationCategoryList = res.data;
    });
  }

  getPrimaryString(controlName): string {
    if (this.relationshipEditForm.get(controlName).value) return 'Yes';
    return 'No';
  }
}
